import React, {useContext, useState} from 'react';
import {Box, Stack, Typography, Button, InputBase, CircularProgress, IconButton, Checkbox, Accordion, AccordionSummary, AccordionDetails} from '@mui/material';
import {HugeiconsIcon} from '@hugeicons/react';
import {Copy01Icon, ChevronDownIcon, PlayIcon, Alert02Icon, CheckIcon, Delete02Icon, Search01Icon} from '@hugeicons/core-free-icons';
import {toast} from 'react-toastify';
import {Context} from '../../../../context/AppContext';
import {ConfirmModalNew} from './ConfirmModalNew';

const controlSx = {
  height: 38,
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1.25,
  borderRadius: "10px",
  bgcolor: "#FAFAFD",
  color: "#6B6B80",
  fontSize: 13,
};

const getSchema = (tool) => tool.input_schema || tool.inputSchema || {};

const getFieldType = (prop) => {
  const type = prop?.type || prop?.anyOf?.find(t => t.type !== "null")?.type;
  if (type === "integer" || type === "number") return "number";
  if (type === "boolean") return "boolean";
  return "text";
};

const buildDefaultArgs = (properties) => Object.fromEntries(
  Object.entries(properties).map(([key, val]) => {
    if ("default" in val) return [key, val.default ?? ""];
    const type = getFieldType(val);
    if (type === "boolean") return [key, false];
    return [key, ""];
  })
);

const ResultPanel = ({result}) => {
  const [copied, setCopied] = useState(false);
  const [pretty, setPretty] = useState(true);

  const rawText = typeof result === 'string' ? result : JSON.stringify(result, null, 2);
  // Un-escapes \n, \t, \r, \" sequences so multi-line text renders as real line breaks instead of literal characters
  const prettyText = rawText.replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\r/g, '').replace(/\\"/g, '"');
  const text = pretty ? prettyText : rawText;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Result copied');
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 0.75}}>
        <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#2F9A6A', textTransform: 'uppercase', letterSpacing: '0.05em'}}>Result</Typography>
        <Stack
          direction="row" alignItems="center" gap={0.5}
          component="label"
          sx={{cursor: 'pointer'}}
        >
          <Checkbox
            size="small"
            checked={pretty}
            onChange={(e) => setPretty(e.target.checked)}
            sx={{p: 0.25, color: '#C9CAD8', '&.Mui-checked': {color: '#8491F7'}}}
          />
          <Typography sx={{fontSize: '12px', color: '#6B6B80', fontWeight: 500}}>Pretty print</Typography>
        </Stack>
      </Stack>
      <Box sx={{position: 'relative', bgcolor: '#FAFAFD', borderRadius: '10px', p: 1.5, maxHeight: {xs: 320, md: 480}, overflow: 'auto'}}>
        <IconButton size="small" onClick={handleCopy} sx={{position: 'absolute', top: 6, right: 6, color: '#8A8AA0'}}>
          <HugeiconsIcon icon={copied ? CheckIcon : Copy01Icon} size={14}/>
        </IconButton>
        <Typography component="pre" sx={{fontSize: '12px', fontFamily: 'monospace', color: '#374151', whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, pr: 3}}>
          {text}
        </Typography>
      </Box>
    </Box>
  );
};

const ToolCard = ({tool, onRequestDelete}) => {
  const {callMCPTool} = useContext(Context);
  const [expanded, setExpanded] = useState(false);
  const schema = getSchema(tool);
  const properties = schema.properties || {};
  const required = schema.required || [];
  const paramCount = Object.keys(properties).length;
  const isActive = tool.is_active ?? tool.active ?? true;

  const [fieldValues, setFieldValues] = useState(() => buildDefaultArgs(properties));
  const [fieldErrors, setFieldErrors] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFieldChange = (key, value) => {
    setFieldValues(prev => ({...prev, [key]: value}));
    if (required.includes(key) && value === "" ) {
      setFieldErrors(prev => ({...prev, [key]: "Required"}));
    } else {
      setFieldErrors(prev => ({...prev, [key]: null}));
    }
  };

  const validate = () => {
    const errors = {};
    required.forEach(key => {
      const val = fieldValues[key];
      if (val === "" || val === null || val === undefined) errors[key] = "Required";
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildArgs = () => Object.fromEntries(
    Object.entries(fieldValues).map(([key, val]) => {
      const type = getFieldType(properties[key]);
      if (type === "number" && val !== "" && val !== null) return [key, Number(val)];
      if (val === "") return [key, null];
      return [key, val];
    })
  );

  const handleCopyTool = () => {
    try {
      const toolDef = {name: tool.name, description: tool.description, inputSchema: schema};
      navigator.clipboard.writeText(JSON.stringify(toolDef, null, 2));
      toast.success('Tool definition copied');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleExecute = async () => {
    if (!validate()) return;
    setRunning(true);
    setResult(null);
    setError(null);
    try {
      const data = await callMCPTool({server_id: tool.server_id, tool_name: tool.name, arguments: buildArgs()});
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Tool call failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(v => !v)}
      disableGutters
      square
      sx={{
        backgroundColor: expanded ? 'rgba(132, 145, 247, 0.05)' : 'transparent',
        borderRadius: '12px',
        boxShadow: 'none',
        '&:before': {display: 'none'},
        transition: 'background-color 0.2s ease',
        '&:hover': {backgroundColor: expanded ? 'rgba(132, 145, 247, 0.07)' : 'rgba(132, 145, 247, 0.05)'},
      }}
    >
      <AccordionSummary
        sx={{
          p: '15px 18px !important',
          '& .MuiAccordionSummary-content': {alignItems: 'center', gap: 1.25, m: '0 !important'},
          '& .MuiAccordionSummary-expandIconWrapper': {display: 'none'},
        }}
      >
        {!isActive && <HugeiconsIcon icon={Alert02Icon} size={15} color="#D9A13A"/>}
        <Typography sx={{fontSize: '13.5px', fontWeight: 600, color: '#23264D', letterSpacing: '-0.01em', flex: 1}}>{tool.name}</Typography>
        <Box sx={{
          width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', bgcolor: expanded ? '#8491F7' : '#F0F0F6', color: expanded ? '#fff' : '#6B6B80',
          transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.3s ease, color 0.3s ease',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
        }}>
          <HugeiconsIcon icon={ChevronDownIcon} size={15}/>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{p: '16px', borderTop: '1px solid #ECECF3', backgroundColor: '#fff'}}>
        <Stack gap={2}>
          {tool.description && (
            <Box>
              <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#8491F7', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75}}>Description</Typography>
              <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>{tool.description}</Typography>
            </Box>
          )}

          {!isActive ? (
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5} sx={{p: 1.5, borderRadius: '10px', bgcolor: 'rgba(217, 161, 58, 0.1)'}}>
              <Typography sx={{fontSize: '12px', color: '#9A6B1F'}}>This tool is inactive and may not execute successfully.</Typography>
              <Button
                size="small"
                startIcon={<HugeiconsIcon icon={Delete02Icon} size={13}/>}
                onClick={() => onRequestDelete?.(tool)}
                sx={{textTransform: 'none', fontWeight: 700, fontSize: '12px', color: '#DC2626', flexShrink: 0, '&:hover': {backgroundColor: 'rgba(220, 38, 38, 0.08)'}}}
              >
                Delete Tool
              </Button>
            </Stack>
          ) : null}

          <Box>
            <Typography sx={{fontSize: '11px', fontWeight: 700, color: '#8491F7', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1}}>
              Try it out
            </Typography>
            {paramCount === 0 ? (
              <Typography sx={{fontSize: '12px', color: '#8A8AA0', fontStyle: 'italic'}}>This tool requires no arguments.</Typography>
            ) : (
              <Stack gap={1.5}>
                {Object.entries(properties).map(([key, prop]) => {
                  const isRequired = required.includes(key);
                  const type = getFieldType(prop);
                  return (
                    <Box key={key}>
                      <Stack direction="row" alignItems="center" gap={0.5} sx={{mb: 0.5}}>
                        <Typography sx={{fontSize: '12px', fontFamily: 'monospace', fontWeight: 600, color: '#23264D'}}>{key}</Typography>
                        {isRequired && <Typography component="span" sx={{color: '#DC2626', fontSize: '12px', fontWeight: 700}}>*</Typography>}
                        {prop.title && prop.title !== key && (
                          <Typography sx={{fontSize: '11px', color: '#8A8AA0'}}>— {prop.title}</Typography>
                        )}
                      </Stack>
                      <Box sx={{...controlSx, ...(fieldErrors[key] && {boxShadow: 'inset 0 0 0 1.5px #DC2626'})}}>
                        <InputBase
                          type={type === 'number' ? 'number' : 'text'}
                          value={fieldValues[key] ?? ""}
                          onChange={e => handleFieldChange(key, e.target.value)}
                          placeholder={isRequired ? "Required" : "Optional"}
                          inputProps={{"aria-label": key}}
                          sx={{flex: 1, fontSize: 13, fontFamily: 'monospace', color: "#23264D"}}
                        />
                      </Box>
                      {fieldErrors[key] && <Typography sx={{fontSize: '11px', color: '#DC2626', mt: 0.5}}>{fieldErrors[key]}</Typography>}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>

          {error && (
            <Box sx={{p: 1.5, borderRadius: '10px', bgcolor: 'rgba(220, 38, 38, 0.08)'}}>
              <Typography sx={{fontSize: '12px', color: '#DC2626'}}>{error}</Typography>
            </Box>
          )}

          {result !== null && <ResultPanel result={result}/>}

          <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
            <Button size="small" startIcon={<HugeiconsIcon icon={Copy01Icon} size={14} />} onClick={handleCopyTool} sx={{textTransform: 'none', fontWeight: 700, fontSize: '13px', color: '#8491F7', '&:hover': {backgroundColor: 'rgba(132, 145, 247, 0.08)'}}}>
              Copy Definition
            </Button>
            <Button
              size="small"
              disabled={running || !isActive}
              startIcon={running ? <CircularProgress size={13} sx={{color: '#fff'}}/> : <HugeiconsIcon icon={PlayIcon} size={14}/>}
              onClick={handleExecute}
              sx={{
                textTransform: 'none', fontWeight: 700, fontSize: '13px', borderRadius: '10px',
                bgcolor: '#8491F7', color: '#fff', px: 2,
                '&:hover': {bgcolor: '#6B78E8'},
                '&.Mui-disabled': {bgcolor: '#D1D5DB', color: '#9CA3AF'},
              }}
            >
              {running ? "Running..." : "Run Tool"}
            </Button>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export const MCPToolsSectionNew = ({ server, onToolDeleted }) => {
  const {deleteInactiveTool} = useContext(Context);
  const tools = server?.tools || [];
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteInactiveTool(pendingDelete.id);
      toast.success('Tool deleted');
      setPendingDelete(null);
      await onToolDeleted?.();
    } catch (err) {
      toast.error('Failed to delete tool');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  if (tools.length === 0) {
    return (<Box sx={{textAlign: 'center', color: '#8A8AA0', py: 6}}><Typography sx={{fontSize: '14px'}}>No tools available for this server</Typography></Box>);
  }

  const term = search.trim().toLowerCase();
  const filteredTools = term
    ? tools.filter(tool => tool.name?.toLowerCase().includes(term) || tool.description?.toLowerCase().includes(term))
    : tools;

  return (
    <Stack gap={1.25}>
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Box sx={{...controlSx, flex: 1}}>
          <HugeiconsIcon icon={Search01Icon} size={16}/>
          <InputBase
            placeholder="Filter tools by name or description"
            value={search}
            onChange={e => setSearch(e.target.value)}
            inputProps={{"aria-label": "Filter tools"}}
            sx={{flex: 1, fontSize: 13, color: "#23264D"}}
          />
        </Box>
        <Typography sx={{fontSize: '12px', color: '#8A8AA0', flexShrink: 0, whiteSpace: 'nowrap'}}>
          Showing {filteredTools.length} of {tools.length}
        </Typography>
      </Stack>

      {filteredTools.length === 0 ? (
        <Box sx={{textAlign: 'center', color: '#8A8AA0', py: 6}}><Typography sx={{fontSize: '14px'}}>No tools match your search</Typography></Box>
      ) : (
        <Stack gap={0.25}>
          {filteredTools.map((tool, index) => (
            <ToolCard
              key={`${server?.id}-${tool.id || tool.name || index}`}
              tool={{...tool, server_id: tool.server_id ?? server?.id}}
              onRequestDelete={setPendingDelete}
            />
          ))}
        </Stack>
      )}

      <ConfirmModalNew
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        title={`Delete "${pendingDelete?.name}"?`}
        description="This inactive tool will be permanently removed from the server."
        confirmText="Delete Tool"
        loading={deleting}
      />
    </Stack>
  );
};
